import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Settings,
  X,
  User,
  Clock,
  Mail,
  Phone,
  Edit,
  Trash2,
  AlertCircle,
  CalendarDays,
} from "lucide-react";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { useTheme } from "../../../context/ThemeContext";

const DateDetailModal = ({
  date,
  reservations,
  onClose,
  onEdit,
  onStatusChange,
  onDelete,
  statusConfig,
  onAddException,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 640 : false,
  );
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);
  const sheetRef = useRef(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchCurrentY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchCurrentY.current = e.touches[0].clientY;
    const diff = touchCurrentY.current - touchStartY.current;
    if (diff > 0 && sheetRef.current) {
      sheetRef.current.style.transform = `translateY(${diff}px)`;
    }
  };

  const handleTouchEnd = () => {
    const diff = touchCurrentY.current - touchStartY.current;
    if (diff > 80) {
      onClose();
    } else if (sheetRef.current) {
      sheetRef.current.style.transform = "";
    }
  };

  const getStatusStyle = (status) => {
    const styles = {
      pending: isDark
        ? "bg-yellow-500/15 text-yellow-400"
        : "bg-yellow-50 text-yellow-700",
      confirmed: isDark
        ? "bg-green-500/15 text-green-400"
        : "bg-green-50 text-green-700",
      cancelled: isDark
        ? "bg-red-500/15 text-red-400"
        : "bg-red-50 text-red-700",
      completed: isDark
        ? "bg-blue-500/15 text-blue-400"
        : "bg-blue-50 text-blue-700",
    };
    return (
      styles[status] ||
      (isDark ? "bg-white/[0.06] text-gray-400" : "bg-gray-100 text-gray-600")
    );
  };

  return (
    <div
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm flex ${isMobile ? "items-end" : "items-center"} justify-center z-50 ${isMobile ? "" : "p-4"}`}
    >
      <motion.div
        ref={sheetRef}
        initial={isMobile ? { y: "100%" } : { scale: 0.95, opacity: 0 }}
        animate={isMobile ? { y: 0 } : { scale: 1, opacity: 1 }}
        transition={
          isMobile
            ? { type: "spring", damping: 28, stiffness: 300 }
            : { type: "spring", duration: 0.3 }
        }
        className={`w-full max-h-[95vh] overflow-y-auto border ${isMobile ? "max-w-full" : "rounded-xl max-w-3xl max-h-[90vh]"} ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
        style={
          isMobile
            ? {
                borderTopLeftRadius: "1rem",
                borderTopRightRadius: "1rem",
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }
            : undefined
        }
      >
        {/* Mobile drag handle */}
        {isMobile && (
          <div
            className="flex justify-center pt-3 pb-1 shrink-0 cursor-grab active:cursor-grabbing"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>
        )}
        {/* Header */}
        <div
          className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`h-8 w-8 rounded-lg flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-100"}`}
            >
              <CalendarDays
                className={`h-4 w-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}
              />
            </div>
            <div>
              <h2
                className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
              >
                {format(date, "EEEE d MMMM yyyy", { locale: nl })}
              </h2>
              <p
                className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
              >
                {reservations.length} afspra
                {reservations.length === 1 ? "ak" : "ken"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onAddException}
              className={`h-8 px-3 rounded-lg text-[12px] font-medium flex items-center gap-1.5 border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
            >
              <Settings className="w-3.5 h-3.5" />
              Uitzondering
            </button>
            <button
              onClick={onClose}
              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {reservations.length === 0 ? (
            <div className="text-center py-12">
              <div
                className={`h-12 w-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50"}`}
              >
                <CalendarDays
                  className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                />
              </div>
              <p
                className={`text-[13px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
              >
                Geen afspraken voor deze dag
              </p>
              <p
                className={`text-[12px] mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
              >
                Maak een nieuwe reservering aan om te beginnen
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {reservations.map((reservation, i) => {
                const StatusIcon =
                  statusConfig[reservation.status]?.icon || AlertCircle;
                return (
                  <motion.div
                    key={reservation.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    className={`rounded-xl p-4 border transition-colors ${isDark ? "bg-white/[0.03] border-white/[0.06] hover:bg-white/[0.05]" : "bg-gray-50/80 border-gray-100 hover:bg-gray-50"}`}
                  >
                    <div className="flex flex-col md:flex-row items-start justify-between gap-3">
                      <div className="flex-1 space-y-2.5 w-full">
                        {/* Name + Time */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div
                              className={`h-6 w-6 rounded-full flex items-center justify-center ${isDark ? "bg-white/[0.06]" : "bg-gray-200/60"}`}
                            >
                              <User
                                className={`w-3 h-3 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              />
                            </div>
                            <span
                              className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {reservation.customer_name}
                            </span>
                          </div>
                          <div
                            className={`flex items-center gap-1.5 text-[12px] font-medium ${isDark ? "text-gray-400" : "text-gray-600"}`}
                          >
                            <Clock className="w-3.5 h-3.5" />
                            {reservation.start_time.slice(0, 5)} –{" "}
                            {reservation.end_time.slice(0, 5)}
                          </div>
                        </div>

                        {/* Contact */}
                        <div
                          className={`flex flex-col sm:flex-row gap-1 sm:gap-4 text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                        >
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3 h-3" />
                            {reservation.customer_email}
                          </div>
                          {reservation.customer_phone && (
                            <div className="flex items-center gap-1.5">
                              <Phone className="w-3 h-3" />
                              {reservation.customer_phone}
                            </div>
                          )}
                        </div>

                        {reservation.service_type && (
                          <span
                            className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-md ${isDark ? "bg-white/[0.06] text-gray-300" : "bg-gray-100 text-gray-600"}`}
                          >
                            {reservation.service_type}
                          </span>
                        )}

                        {reservation.notes && (
                          <p
                            className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-500"}`}
                          >
                            {reservation.notes}
                          </p>
                        )}

                        {/* Status */}
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            value={reservation.status}
                            onChange={(e) =>
                              onStatusChange(reservation, e.target.value)
                            }
                            className={`h-7 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                          >
                            <option value="pending">In afwachting</option>
                            <option value="confirmed">Bevestigd</option>
                            <option value="cancelled">Geannuleerd</option>
                            <option value="completed">Voltooid</option>
                          </select>
                          <span
                            className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${getStatusStyle(reservation.status)}`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {statusConfig[reservation.status]?.label}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-1 w-full md:w-auto justify-end">
                        <button
                          onClick={() => onEdit(reservation)}
                          className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-500 hover:text-white hover:bg-white/[0.06]" : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"}`}
                          title="Bewerken"
                        >
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onDelete(reservation.id)}
                          className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-red-400/60 hover:text-red-400 hover:bg-red-500/10" : "text-red-400 hover:text-red-600 hover:bg-red-50"}`}
                          title="Verwijderen"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DateDetailModal;
